import React, { useMemo, useState } from "react";
import { Alert, Box, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import moment from "moment";
import EChartBase from "../../../components/charts/EChartBase";
import { useStrategyGreeksHistory, useStrategyRiskHistory, useStrategyVarHistory } from "../hooks/useStrategyRiskHistory";
import { isSpacStrategyType } from "../utils/investmentTypeGrouping";
import { useRiskHistory } from "../../firmwide-summary/hooks/useRiskHistory";
import {
  ChartCard,
  COLORS,
  formatSigned,
  Legend,
  MODE_EXPOSURE,
  MODE_PNL,
  RANGES,
} from "../../firmwide-summary/components/StrategyRiskAnalyticsDialog";

const ALL = "__ALL__";
const KIND_STANDALONE = "standalone";
const KIND_CONTRIBUTION = "contribution";
const toggleSx = { px: 1.5, py: 0.25, fontSize: 12, fontWeight: 700 };

const gmv = (p) => (p.lmv || 0) + (p.smv || 0);

const GREEK_COLORS = ["#1e3a5f", "#059669", "#d97706", "#7c3aed", "#0891b2", "#e05a5a"];
const maxAbs = (vals) => vals.reduce((m, v) => (v == null || !Number.isFinite(v) ? m : Math.max(m, Math.abs(v))), 0);

// Daily greeks shock P&L as one line per scenario label. A label can change over time (Delta became Beta on
// 2026-10-07); each label is its own line, so the switch shows as one line ending and another starting.
// A greek that is zero (or absent) on every date in view is dropped: a flat line at zero says nothing.
const activeGreeks = (scenarios, rows) =>
  scenarios.filter((name) => rows.some((r) => Number.isFinite(r.greeks?.[name]) && r.greeks[name] !== 0));

// `colorOf` keeps each greek's colour fixed (by its place in the full scenario list) however the others are toggled.
const buildGreeksOption = (dates, shown, rows, colorOf) => {
  const series = shown.map((name) => ({
    name,
    type: "line",
    smooth: 0.2,
    connectNulls: false,
    showSymbol: dates.length <= 40,
    symbolSize: 5,
    lineStyle: { width: 2, color: colorOf(name) },
    itemStyle: { color: colorOf(name) },
    data: rows.map((r) => r.greeks?.[name] ?? null),
  }));
  const inK = maxAbs(series.flatMap((s) => s.data)) < 10000;
  return {
    grid: { left: 4, right: 4, top: 14, bottom: 0, containLabel: true },
    tooltip: {
      trigger: "axis",
      backgroundColor: "rgba(255, 255, 255, 0.97)",
      borderColor: "#e2e8f0",
      textStyle: { color: "#1e293b", fontSize: 12 },
      formatter: (params) =>
        `<div style="font-weight:600;margin-bottom:4px">${moment(params[0].axisValue).format("ddd, MMM D YYYY")}</div>` +
        params
          .filter((p) => p.value != null)
          .map((p) => `<div style="display:flex;justify-content:space-between;gap:16px"><span>${p.marker}${p.seriesName}</span><b>${formatSigned(p.value, inK)}</b></div>`)
          .join(""),
    },
    xAxis: { type: "category", data: dates, axisTick: { show: false }, axisLine: { lineStyle: { color: "#e2e8f0" } }, axisLabel: { color: COLORS.axis, fontSize: 11, hideOverlap: true, formatter: (d) => moment(d).format("MMM D") } },
    yAxis: { type: "value", axisLabel: { color: COLORS.axis, fontSize: 11, formatter: (v) => formatSigned(v, inK).replace(".00", "") }, splitLine: { lineStyle: { color: COLORS.grid } } },
    series,
  };
};

// Greeks P&L chart for the focused strategy (or, for "All strategies", the type's greeks = sum over its strategies).
// Greeks can be switched on and off with the chips; every non-zero greek starts on, and a greek the user turned off
// stays off while moving between strategies.
const GreeksCard = ({ title, dates, scenarios, rows, pending }) => {
  const [hidden, setHidden] = useState(() => new Set());
  const available = useMemo(() => activeGreeks(scenarios, rows), [scenarios, rows]);
  const shown = available.filter((name) => !hidden.has(name));
  const colorOf = (name) => GREEK_COLORS[scenarios.indexOf(name) % GREEK_COLORS.length];
  const option = useMemo(() => buildGreeksOption(dates, shown, rows, colorOf), [dates, shown.join("|"), rows, scenarios]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = (name) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  return (
    <Box sx={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "6px", p: 2.5 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 700, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        Greeks P&amp;L{title ? ` \u2014 ${title}` : ""}
      </Typography>
      <Typography sx={{ fontSize: 12, color: "#64748b", mb: 1 }}>
        Shock-scenario P&amp;L by greek (DV01, CS01, Delta/Beta, Vega), from the portfolio greeks runs; not dependent on the VaR simulations.
      </Typography>
      {available.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1 }} role="group" aria-label="greeks to display">
          {available.map((name) => {
            const on = !hidden.has(name);
            return (
              <Box
                key={name}
                component="button"
                type="button"
                aria-pressed={on}
                onClick={() => toggle(name)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.75,
                  px: 1.25,
                  py: 0.25,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: "pointer",
                  borderRadius: "999px",
                  border: `1px solid ${on ? colorOf(name) : "#cbd5e1"}`,
                  backgroundColor: on ? "#fff" : "#f1f5f9",
                  color: on ? "#1e293b" : "#94a3b8",
                  fontFamily: "inherit",
                }}
              >
                <Box component="span" sx={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: on ? colorOf(name) : "#cbd5e1" }} />
                {name}
              </Box>
            );
          })}
        </Box>
      )}
      {pending ? (
        <EChartBase option={null} loading height={260} />
      ) : (
        <EChartBase
          option={option}
          height={260}
          emptyMessage={available.length === 0 ? "No non-zero greeks in this range" : "No greeks selected"}
          notMerge
        />
      )}
    </Box>
  );
};

// Tiny inline trend line for a rail row.
const Sparkline = ({ values, color }) => {
  const nums = values.filter((v) => v != null && Number.isFinite(v));
  if (nums.length < 2) return <Box sx={{ width: 64, height: 22 }} />;
  const min = Math.min(...nums);
  const span = Math.max(...nums) - min || 1;
  const step = 64 / (nums.length - 1);
  const pts = nums.map((v, i) => `${(i * step).toFixed(1)},${(20 - ((v - min) / span) * 18).toFixed(1)}`).join(" ");
  return (
    <svg width="64" height="22" viewBox="0 0 64 22" aria-hidden="true">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
};

const RailRow = ({ label, subtitle, trend, selected, onClick }) => (
  <Box
    role="button"
    tabIndex={0}
    aria-pressed={selected}
    onClick={onClick}
    onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onClick())}
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 1,
      px: 1.5,
      py: 1,
      cursor: "pointer",
      borderLeft: `3px solid ${selected ? "#1e3a5f" : "transparent"}`,
      backgroundColor: selected ? "#eef2f7" : "transparent",
      borderBottom: "1px solid #f1f5f9",
      "&:hover": { backgroundColor: selected ? "#eef2f7" : "#f8fafc" },
    }}
  >
    <Box sx={{ minWidth: 0 }}>
      <Typography noWrap sx={{ fontSize: 11, fontWeight: 700, color: "#1e293b", textTransform: "uppercase", letterSpacing: "0.5px" }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 11, fontWeight: 500, color: "#64748b", fontVariantNumeric: "tabular-nums" }}>{subtitle}</Typography>
    </Box>
    <Sparkline values={trend} color={selected ? "#1e3a5f" : COLORS.axis} />
  </Box>
);

/**
 * Risk Analytics tab for the strategy-type drill-down: daily P&L / exposure vs VaR 95% / 99% broken
 * down by strategy_name within the type. The left rail ranks strategies by latest VaR 95% (largest loss first); clicking one
 * puts its chart in focus, and "All strategies" repeats the type-level Risk Trends chart. A toggle picks the
 * strategy-level VaR: "Stand-alone" is the strategy's own VaR (its own 5th / 1st percentile; does not sum to the type's
 * VaR), "Contribution" is its P&L in the type's VaR scenario (sums to the type's VaR). Exposure / P&L load first;
 * VaR streams in separately (the server answers from cache and reports days still pending).
 */
export default function StrategyTypeRiskTrends({ strategyTypeName, asOfDate, enabled = true }) {
  const [rangeKey, setRangeKey] = useState("1M");
  const [mode, setMode] = useState(MODE_PNL);
  const [selected, setSelected] = useState(ALL);
  const [kind, setKind] = useState(KIND_STANDALONE);
  const months = RANGES.find((r) => r.key === rangeKey).months;
  const endDate = asOfDate ? moment(asOfDate).format("YYYY-MM-DD") : null;
  const startDate = endDate ? moment(endDate).subtract(months, "months").format("YYYY-MM-DD") : null;

  const { data, isLoading, isError } = useStrategyRiskHistory(strategyTypeName, startDate, endDate, enabled);
  const varQuery = useStrategyVarHistory(strategyTypeName, startDate, endDate, kind, enabled);
  // keepPreviousData can hold the other kind's numbers for a moment after the toggle: never show those under this label.
  const varData = varQuery.data && varQuery.data.kind === kind ? varQuery.data : null;
  const varPending = varQuery.isLoading || !varData || varData.pending > 0;
  const strategies = useMemo(() => {
    const base = data?.strategies || {};
    const out = {};
    for (const [name, pts] of Object.entries(base)) {
      const byDate = new Map((varData?.strategies?.[name] || []).map((v) => [v.date, v]));
      out[name] = pts.map((p) => ({ ...p, var_95: byDate.get(p.date)?.var_95 ?? null, var_99: byDate.get(p.date)?.var_99 ?? null }));
    }
    return out;
  }, [data, varData]);
  const showGreeks = !isSpacStrategyType(strategyTypeName); // no portfolio greeks for SPAC, as on the strategy cards
  const greeksQuery = useStrategyGreeksHistory(strategyTypeName, startDate, endDate, enabled && showGreeks);
  const greeksData = greeksQuery.data;
  const latestVar = (pts) => [...pts].reverse().find((p) => p.var_95 != null)?.var_95 ?? null;

  const ranked = useMemo(
    () =>
      Object.entries(strategies)
        .filter(([, pts]) => pts.some((p) => gmv(p) > 0 || p.net_pnl))
        .sort((a, b) => {
          // Largest loss first (VaR is negative); strategies with no VaR go last, ordered by GMV.
          const av = latestVar(a[1]);
          const bv = latestVar(b[1]);
          if (av == null && bv == null) return gmv(b[1][b[1].length - 1] || {}) - gmv(a[1][a[1].length - 1] || {});
          if (av == null) return 1;
          if (bv == null) return -1;
          return av - bv;
        })
        .map(([name]) => name),
    [strategies],
  );
  // "All strategies" is the type-level chart, identical to Risk Trends on the Risk Framework page.
  const typeHistory = useRiskHistory(startDate, endDate, enabled);
  const typePoints = typeHistory.data?.series?.[strategyTypeName] || [];
  const focus = selected !== ALL && strategies[selected] ? selected : ALL;
  // "All strategies" greeks = the type's greeks: each scenario summed over every strategy, per date.
  const greeksRows = useMemo(() => {
    if (!greeksData?.dates) return [];
    if (focus !== ALL) return greeksData.strategies?.[focus] || greeksData.dates.map((date) => ({ date, greeks: null }));
    return greeksData.dates.map((date, i) => {
      const sum = {};
      let any = false;
      for (const pts of Object.values(greeksData.strategies || {})) {
        const g = pts[i]?.greeks;
        if (!g) continue;
        any = true;
        for (const [k, v] of Object.entries(g)) sum[k] = (sum[k] || 0) + v;
      }
      return { date, greeks: any ? sum : null };
    });
  }, [greeksData, focus]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1.5 }}>
        <Typography variant="body2" color="text.secondary">
          {mode === MODE_PNL ? "Daily net P&L" : "Daily LMV / SMV"} vs VaR 95% / 99%
          {endDate ? `, through ${endDate}` : ""}.
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ToggleButtonGroup value={mode} exclusive size="small" onChange={(e, next) => next && setMode(next)}>
            <ToggleButton value={MODE_PNL} sx={toggleSx}>P&amp;L</ToggleButton>
            <ToggleButton value={MODE_EXPOSURE} sx={toggleSx}>Exposures</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup
            value={kind}
            exclusive
            size="small"
            aria-label="strategy VaR basis"
            onChange={(e, next) => next && setKind(next)}
          >
            <ToggleButton value={KIND_STANDALONE} sx={toggleSx} title="Each strategy's own VaR: the 5th / 1st percentile of its own simulated P&L. Does not sum to the type's VaR.">Stand-alone VaR</ToggleButton>
            <ToggleButton value={KIND_CONTRIBUTION} sx={toggleSx} title="Each strategy's P&L in the simulation that is the strategy type's VaR scenario. Sums to the type's VaR.">Contribution to Strategy Type VaR</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup value={rangeKey} exclusive size="small" onChange={(e, next) => next && setRangeKey(next)}>
            {RANGES.map((r) => (
              <ToggleButton key={r.key} value={r.key} sx={toggleSx}>{r.label}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      </Box>
      <Box sx={{ mb: 1.5 }}>
        <Legend mode={mode} />
      </Box>

      {isError && <Alert severity="error" sx={{ mb: 2 }}>Could not load strategy risk history.</Alert>}
      {varData && !varData.var_available && varData.pending === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>Strategy VaR history is unavailable (Risk Monitor could not be reached); showing exposures and P&L only.</Alert>
      )}
      {varQuery.isError && <Alert severity="warning" sx={{ mb: 2 }}>Could not load strategy VaR; showing exposures and P&L only.</Alert>}
      {focus !== ALL && varPending && !varQuery.isError && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
          {varData ? `Loading VaR… ${varData.pending} of ${varData.dates.length} days remaining` : "Loading VaR…"}
        </Typography>
      )}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(240px, 300px) 1fr" }, gap: 2, alignItems: "stretch" }}>
        {/* The rail is as tall as the charts column (down to the end of the greeks chart) and scrolls inside that height. */}
        <Box sx={{ position: "relative", minHeight: 320 }}>
        <Box sx={{ position: "absolute", inset: 0, border: "1px solid #e2e8f0", borderRadius: "6px", overflowY: "auto", backgroundColor: "#fff" }}>
          <RailRow
            label="All strategies"
            subtitle={`${strategyTypeName}`}
            trend={typePoints.map((p) => (mode === MODE_PNL ? p.net_pnl : gmv(p)))}
            selected={focus === ALL}
            onClick={() => setSelected(ALL)}
          />
          {ranked.map((name) => {
            const pts = strategies[name];
            const latest = latestVar(pts);
            return (
              <RailRow
                key={name}
                label={name}
                subtitle={`VaR 95% ${latest != null ? formatSigned(latest) : "—"}`}
                trend={pts.map((p) => (mode === MODE_PNL ? p.net_pnl : gmv(p)))}
                selected={focus === name}
                onClick={() => setSelected(name)}
              />
            );
          })}
        </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
          {(focus === ALL ? typeHistory.isLoading : isLoading) ? (
            <EChartBase option={null} loading height={340} />
          ) : focus === ALL ? (
            <ChartCard key={`${strategyTypeName}-${mode}`} title={strategyTypeName} points={typePoints} mode={mode} hero />
          ) : (
            <ChartCard key={`${focus}-${mode}`} title={focus} points={strategies[focus]} mode={mode} hero keepVarSign />
          )}
          {showGreeks && (greeksQuery.isLoading || (greeksData && greeksData.greeks_available)) && (
            <GreeksCard
              title={focus === ALL ? strategyTypeName : focus}
              dates={greeksData?.dates || []}
              scenarios={greeksData?.scenarios || []}
              rows={greeksRows}
              pending={greeksQuery.isLoading}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
}
